import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload, Pencil } from 'lucide-react';
import { api } from '../services/api';

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function extractError(err) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
  return err.message || 'Unknown error';
}

function TemplateModal({ open, onClose, onSubmitted, workspaceId, mode, template }) {
  const { t } = useTranslation('receipts');
  const isEdit = mode === 'edit';
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit && template) {
      setName(template.name);
    } else {
      setName('');
    }
    setFile(null);
    setError(null);
    setBusy(false);
  }, [open, isEdit, template]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && (!file || !name.trim())) return;
    if (isEdit && !name.trim() && !file) return;
    setBusy(true);
    setError(null);
    try {
      let result;
      if (isEdit) {
        result = await api.updateReceiptTemplate(workspaceId, template.id, {
          name: name.trim() !== template.name ? name.trim() : undefined,
          file: file || undefined,
        });
      } else {
        result = await api.createReceiptTemplate(workspaceId, file, name.trim());
      }
      onSubmitted(result);
      onClose();
    } catch (err) {
      const message = extractError(err);
      setError(t(isEdit ? 'templates.updateError' : 'templates.uploadError', { message }));
      setBusy(false);
    }
  };

  return (
    <div className="receipts-modal-overlay" onClick={onClose}>
      <form className="receipts-modal" onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <h2>{t(isEdit ? 'templates.editModalTitle' : 'templates.uploadModalTitle')}</h2>

        <div className="receipts-field">
          <label>{t('templates.nameLabel')}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('templates.namePlaceholder')}
            maxLength={120}
            autoFocus
            required
          />
        </div>

        <div className="receipts-field">
          <label>{t(isEdit ? 'templates.fileLabelOptional' : 'templates.fileLabel')}</label>
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required={!isEdit}
          />
        </div>

        {error && <div className="receipts-empty-hint">{error}</div>}

        <div className="receipts-modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t('templates.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {t('templates.save')}
          </button>
        </div>
      </form>
    </div>
  );
}

function ReceiptTemplatesManager({ workspaceId, onChange }) {
  const { t } = useTranslation('receipts');
  const [templates, setTemplates] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const list = await api.listReceiptTemplates(workspaceId);
    setTemplates(list);
    if (onChange) onChange(list);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const onDelete = async (template) => {
    if (!window.confirm(t('templates.deleteConfirm', { name: template.name }))) return;
    await api.deleteReceiptTemplate(workspaceId, template.id);
    await reload();
  };

  const onSubmitted = async () => {
    await reload();
  };

  return (
    <section className="receipts-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>{t('templates.title')}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {t('templates.subtitle')}
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
          <Upload size={16} style={{ marginRight: 6 }} />
          {t('templates.uploadButton')}
        </button>
      </div>

      {templates === null && <div>{t('templates.loading')}</div>}

      {templates !== null && templates.length === 0 && (
        <div className="receipts-empty-hint">{t('templates.empty')}</div>
      )}

      {templates && templates.length > 0 && (
        <ul className="receipts-template-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {templates.map((tpl) => (
            <li key={tpl.id} className="receipts-template-item">
              <div className="receipts-template-info">
                <div className="receipts-template-name">{tpl.name}</div>
                <div className="receipts-template-date">
                  {t('templates.createdAt')}: {formatDate(tpl.created_at)}
                </div>
              </div>
              <button
                type="button"
                className="receipts-file-remove"
                onClick={() => setEditing(tpl)}
                title={t('templates.editButton')}
              >
                <Pencil size={16} />
              </button>
              <button
                type="button"
                className="receipts-file-remove"
                onClick={() => onDelete(tpl)}
                title={t('templates.cancel')}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <TemplateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmitted={onSubmitted}
        workspaceId={workspaceId}
        mode="create"
        template={null}
      />
      <TemplateModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSubmitted={onSubmitted}
        workspaceId={workspaceId}
        mode="edit"
        template={editing}
      />
    </section>
  );
}

export default ReceiptTemplatesManager;
